import { addMinutes } from 'date-fns';
import { upsertAppointment } from '../../api/appointments';
import AvailabilityService from '../../customer-landing/storefront-client-portal/bundle/components/booking/services/AvailabilityService';
import {
  canBookWithOverlapCount,
  computeAppointmentDurationMinutes,
  countOverlappingAppointments,
  getDoubleBookingSettings,
} from '../../shared/booking/double-booking/employeeSlotAvailability';
import { resolveAppointmentEmployeeId } from '../utils/normalizeCompanyAppointment';
import { MEETINGS_COLUMN_ID, UNASSIGNED_COLUMN_ID } from '../utils/columnMatch';
import { snapDateTimeToSlot } from './calendarDragTime';

function resolveBufferTime(appointment) {
  const raw = appointment?.serviceBufferTime ?? appointment?.bufferTime ?? 0;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function resolveAddons(appointment) {
  return appointment?.selectedAddons || appointment?.addons || [];
}

export function isSameCalendarDrop(appointment, sourceColumnId, targetEmployeeId, newStartTime) {
  if (!appointment?.dateTime || !newStartTime) return false;

  const currentEmployeeId = resolveAppointmentEmployeeId(appointment);
  const sameColumn =
    targetEmployeeId === sourceColumnId ||
    (currentEmployeeId && targetEmployeeId === currentEmployeeId);

  if (!sameColumn) return false;

  const originalSlot = snapDateTimeToSlot(appointment.dateTime);
  const targetSlot = snapDateTimeToSlot(newStartTime);
  if (!originalSlot || !targetSlot) return false;

  return originalSlot.getTime() === targetSlot.getTime();
}

export async function checkCalendarDropAvailability({
  companyId,
  companyData,
  appointment,
  targetEmployeeId,
  newStartTime,
}) {
  if (!companyId || !appointment?.id || !targetEmployeeId || !newStartTime) {
    return { isAvailable: false, reason: 'Missing reschedule details.' };
  }

  if (targetEmployeeId === MEETINGS_COLUMN_ID) {
    return { isAvailable: false, reason: 'Appointments cannot be moved to the Meetings column.' };
  }

  if (targetEmployeeId === UNASSIGNED_COLUMN_ID) {
    return { isAvailable: false, reason: 'Select a staff member column.' };
  }

  const duration = computeAppointmentDurationMinutes(appointment);
  const bufferTime = resolveBufferTime(appointment);
  const addons = resolveAddons(appointment);
  const addonsDuration = addons.reduce(
    (total, addon) =>
      total +
      (parseInt(addon.duration, 10) || 0) +
      (parseInt(addon.bufferTime, 10) || 0),
    0
  );
  const totalBlockedMinutes = duration + bufferTime + addonsDuration;
  const slotEnd = addMinutes(newStartTime, totalBlockedMinutes);
  const { allowDoubleBooking, maxConcurrentPerSlot } = getDoubleBookingSettings(companyData);

  const queryStart = addMinutes(newStartTime, -totalBlockedMinutes);
  const queryEnd = addMinutes(slotEnd, totalBlockedMinutes);

  const busySlots = await AvailabilityService.fetchBusySlotsInRange(
    companyId,
    queryStart,
    queryEnd
  );

  const appointments = busySlots.filter((slot) => slot.id !== appointment.id);
  const overlappingCount = countOverlappingAppointments({
    appointments,
    employeeId: targetEmployeeId,
    slotStart: newStartTime,
    slotEnd,
  });

  const isAvailable = canBookWithOverlapCount(overlappingCount, {
    allowDoubleBooking,
    maxConcurrentPerSlot,
  });

  if (isAvailable) {
    return { isAvailable: true, reason: null };
  }

  if (!allowDoubleBooking) {
    return {
      isAvailable: false,
      reason: 'That staff member already has an appointment at this time.',
    };
  }

  return {
    isAvailable: false,
    reason: 'This time slot is at capacity for that staff member.',
  };
}

export async function rescheduleCalendarAppointment({
  companyId,
  appointment,
  newStartTime,
  targetEmployeeId,
  targetEmployeeName,
  user,
}) {
  if (!companyId || !appointment?.id || !newStartTime || !user?.uid) {
    throw new Error('Missing required reschedule fields.');
  }

  const duration = computeAppointmentDurationMinutes(appointment);
  const newEndTime = addMinutes(newStartTime, duration);
  const currentEmployeeId = resolveAppointmentEmployeeId(appointment);
  const now = new Date().toISOString();
  const actor = {
    userId: user.uid,
    name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.displayName || 'Staff',
    role: user.role || user.type || 'staff',
  };
  const previousDateTime = appointment.dateTime instanceof Date
    ? appointment.dateTime.toISOString()
    : appointment.dateTime;

  const updates = {
    dateTime: newStartTime.toISOString(),
    endTime: newEndTime.toISOString(),
    lastUpdated: now,
    rescheduleStatus: 'rescheduled',
    status: appointment.status || 'confirmed',
    originalDateTime: appointment.originalDateTime || previousDateTime,
    rescheduledDateTime: newStartTime.toISOString(),
    rescheduledAt: now,
    rescheduledBy: actor,
    reminderSent: false,
    reminderSMSSent: false,
    automatedCheckInReminderSent: false,
    rescheduleHistory: [
      ...(Array.isArray(appointment.rescheduleHistory) ? appointment.rescheduleHistory : []),
      {
        from: previousDateTime,
        to: newStartTime.toISOString(),
        at: now,
        by: actor,
      },
    ],
  };

  if (
    targetEmployeeId &&
    targetEmployeeId !== MEETINGS_COLUMN_ID &&
    targetEmployeeId !== UNASSIGNED_COLUMN_ID &&
    targetEmployeeId !== currentEmployeeId
  ) {
    updates.employeeId = targetEmployeeId;
    if (targetEmployeeName) {
      updates.employeeName = targetEmployeeName;
    }
  }

  await upsertAppointment({
    companyId,
    id: appointment.id,
    appointment: updates,
  });

  return { newStartTime, newEndTime, employeeId: updates.employeeId || currentEmployeeId };
}

export async function checkCalendarDropAvailabilityForItem(params) {
  if (params.appointment?.type === 'meeting') {
    return { isAvailable: false, reason: 'Meetings cannot be rescheduled from the calendar grid yet.' };
  }
  return checkCalendarDropAvailability(params);
}

export async function rescheduleCalendarItem(params) {
  if (params.appointment?.type === 'meeting') {
    throw new Error('Meetings cannot be rescheduled from the calendar grid yet.');
  }
  return rescheduleCalendarAppointment(params);
}
